import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { MatButtonModule } from '@angular/material/button';
import { LearnMoreModalComponent } from './learn-more-modal/learn-more-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { ProgressTrackerComponent, StageStatus, Status } from '../../progress-tracker/progress-tracker.component';
import { map, Observable } from 'rxjs';
import { A2PRegistrationService } from '../a2p-registration.service';
import { RegistrationStage } from '../a2p-registration.constants';
import {
  A2PRegistrationStatusInterface,
  BrandStatus,
  MessagingCampaignStatus,
  ValidationStatus,
} from '@vendasta/smsv2';

@Component({
  selector: 'sms-info-banner',
  imports: [CommonModule, MatCardModule, GalaxyBadgeModule, MatButtonModule, TranslateModule, ProgressTrackerComponent],
  templateUrl: './info-banner.component.html',
  styleUrls: ['./info-banner.component.scss'],
})
export class InfoBannerComponent {
  stages$: Observable<StageStatus[]>;
  constructor(
    private dialog: MatDialog,
    private readonly smsRegistrationService: A2PRegistrationService,
  ) {
    this.stages$ = this.smsRegistrationService.A2PRegistrationState$.pipe(
      map((state) => {
        if (state.RegistrationStage === RegistrationStage.NotSubmitted) {
          return [
            { name: 'Submit information', status: Status.NotStarted },
            { name: 'Information review', status: Status.NotStarted },
            { name: 'Final approval', status: Status.NotStarted },
          ];
        }

        return [
          { name: 'Information submitted', status: Status.Completed },
          this.getInformationStageStatus(state?.a2pRegistrationStatus),
          this.getMessagingCampaignStageStatus(state?.a2pRegistrationStatus),
        ];
      }),
    );
  }

  openLearnMoreModal(): void {
    this.dialog.open(LearnMoreModalComponent, { width: '600px' });
  }

  private getInformationStageStatus(registrationStatus: A2PRegistrationStatusInterface): StageStatus {
    let informationStatus: Status = Status.InProgress; // Default to in progress, this assumes info has been submitted
    const secondaryBusinessProfileStatus = registrationStatus?.secondaryBusinessProfile?.status;
    const trustBundleStatus = registrationStatus?.trustBundle?.status;
    const brandStatus = registrationStatus?.brand?.status;

    if (brandStatus === BrandStatus.BRAND_STATUS_APPROVED) {
      informationStatus = Status.Completed;
    } else if (
      secondaryBusinessProfileStatus === ValidationStatus.VALIDATION_STATUS_NON_COMPLIANT ||
      trustBundleStatus === ValidationStatus.VALIDATION_STATUS_NON_COMPLIANT ||
      brandStatus === BrandStatus.BRAND_STATUS_FAILED
    ) {
      informationStatus = Status.Failed;
    }

    switch (informationStatus) {
      case Status.InProgress:
        return { name: 'Information review', status: Status.InProgress };
      case Status.Completed:
        return { name: 'Information approved', status: Status.Completed };
      case Status.Failed:
        return { name: 'Information rejected', status: Status.Failed };
      default:
        return { name: 'Information review', status: Status.InProgress };
    }
  }

  private getMessagingCampaignStageStatus(registrationStatus: A2PRegistrationStatusInterface): StageStatus {
    const informationStageStatus = this.getInformationStageStatus(registrationStatus);
    let campaignStatus: Status =
      informationStageStatus.status === Status.Completed ? Status.InProgress : Status.NotStarted;

    const messagingCampaignStatus = registrationStatus?.messagingCampaign?.status;
    if (messagingCampaignStatus === MessagingCampaignStatus.MESSAGING_CAMPAIGN_STATUS_VERIFIED) {
      campaignStatus = Status.Completed;
    } else if (messagingCampaignStatus === MessagingCampaignStatus.MESSAGING_CAMPAIGN_STATUS_FAILED) {
      campaignStatus = Status.Failed;
    } else if (messagingCampaignStatus === MessagingCampaignStatus.MESSAGING_CAMPAIGN_STATUS_PENDING) {
      campaignStatus = Status.InProgress;
    }

    switch (campaignStatus) {
      case Status.InProgress:
        return { name: 'Final approval', status: Status.InProgress };
      case Status.Completed:
        return { name: 'Approved', status: Status.Completed };
      case Status.Failed:
        return { name: 'Not approved', status: Status.Failed };
      default:
        return { name: 'Final approval', status: Status.NotStarted };
    }
  }
}
